import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoTranslation } from './useAutoTranslation';
import { useAnalytics } from './useAnalytics';
import { useSection } from './useSection';

interface UseDetailCardTranslationOptions {
  itemId: string;
  itemName: string;
  description: string;
  offer?: string;
  descriptions?: Record<string, string>;
  offers?: Record<string, string>;
  tableId: string;
  sectionType: string;
}

export function useDetailCardTranslation({
  itemId,
  itemName,
  description,
  offer,
  descriptions,
  offers,
  tableId,
  sectionType
}: UseDetailCardTranslationOptions) {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const section = useSection();
  const [translatedDescriptionState, setTranslatedDescriptionState] = useState<string | null>(null);
  const [translatedOfferState, setTranslatedOfferState] = useState<string | null>(null);

  const analytics = useAnalytics({
    sectionId: section.id,
    sectionName: section.names.fr,
    sectionType
  });

  const {
    translate: translateDescription,
    translating: translatingDescription,
    error: descriptionError
  } = useAutoTranslation({
    description,
    currentLanguage,
    context: {
      tableId,
      itemId,
      sectionType,
      contentType: 'description'
    }
  });

  const {
    translate: translateOffer,
    translating: translatingOffer,
    error: offerError
  } = useAutoTranslation({
    description: offer || '',
    currentLanguage,
    context: {
      tableId,
      itemId,
      sectionType,
      contentType: 'offer'
    }
  });

  useEffect(() => {
    const checkAndTranslateDescription = async () => {
      const currentLang = i18n.language;
      const currentDescription = descriptions?.[currentLang];
      const isDescriptionMissing = !currentDescription ||
        currentDescription.trim() === '' ||
        currentDescription === '\n';

      if (isDescriptionMissing) {
        const translated = await translateDescription();
        if (translated) {
          if (descriptions) {
            descriptions[currentLang] = translated;
          }
          setTranslatedDescriptionState(translated);

          analytics.trackEvent('user', 'auto_translation', {
            item_id: itemId,
            item_name: itemName,
            from_language: 'fr',
            to_language: currentLang,
            success: true,
            text_length: translated.length,
            content_type: 'description'
          });
        }
      }
    };

    checkAndTranslateDescription();
  }, [descriptions, i18n.language, translateDescription, itemId, itemName, analytics]);

  useEffect(() => {
    const checkAndTranslateOffer = async () => {
      if (!offer) return;
      const currentLang = i18n.language;
      const currentOffer = offers?.[currentLang];
      const isOfferMissing = !currentOffer ||
        currentOffer.trim() === '' ||
        currentOffer === '\n';

      if (isOfferMissing) {
        const translated = await translateOffer();
        if (translated) {
          if (offers) {
            offers[currentLang] = translated;
          }
          setTranslatedOfferState(translated);

          analytics.trackEvent('user', 'auto_translation', {
            item_id: itemId,
            item_name: itemName,
            from_language: 'fr',
            to_language: currentLang,
            success: true,
            text_length: translated.length,
            content_type: 'offer'
          });
        }
      }
    };

    if (offer) {
      checkAndTranslateOffer();
    }
  }, [offers, offer, i18n.language, translateOffer, itemId, itemName, analytics, currentLanguage]);

  const getDescription = useCallback(() => {
    if (translatedDescriptionState) return translatedDescriptionState;
    if (descriptions?.[currentLanguage]) return descriptions[currentLanguage];
    return description;
  }, [translatedDescriptionState, descriptions, currentLanguage, description]);

  const getOffer = useCallback(() => {
    if (translatedOfferState) return translatedOfferState;
    if (offers?.[currentLanguage]) return offers[currentLanguage];
    return offer;
  }, [translatedOfferState, offers, currentLanguage, offer]);

  return {
    getDescription,
    getOffer,
    translatingDescription,
    translatingOffer,
    descriptionError,
    offerError,
    currentLanguage
  };
}
